import { configureScope } from '@sentry/react';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
} from 'react';
import { Redirect } from 'react-router-dom';

import { Permission } from '../../graphql.generated';
import paths from '../../paths.json';
import { useAuthentication } from '../AuthenticationProvider';
import Loading from '../Loading';

import { UserQuery, useUserQuery } from './User.generated';

type User = UserQuery['me'];

export const UserContext = createContext<{
  refetch: () => void;
  user: User;
} | null>(null);

export const useUser = (): User => {
  const user = useContext(UserContext)?.user;

  if (!user) {
    throw Error('No <UserProvider />');
  }

  return user;
};

export const usePermissions = (): Permission[] => {
  const user = useUser();

  if (!user.permissions) {
    throw Error('User has no Permissions');
  }

  return user.permissions;
};

const UserProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { accessToken } = useAuthentication();

  const { data, loading, error, refetch } = useUserQuery({
    onCompleted: (data) => {
      configureScope((scope) => {
        scope.setUser(data.me);
      });
    },
  });

  const user = data?.me;

  useEffect(() => {
    if (accessToken) {
      void refetch();
    }
  }, [accessToken, refetch]);

  if (loading) {
    return <Loading />;
  }

  if (error || !user) {
    return (
      <Redirect
        push
        to={{
          pathname: paths.login,
          state: {
            error: 'Failed to fetch user. Please login again.',
          },
        }}
      />
    );
  }

  return (
    <UserContext.Provider value={{ refetch, user }}>
      {children}
    </UserContext.Provider>
  );
};

UserProvider.displayName = 'UserProvider';

export default UserProvider;
